import { useCallback, useEffect, useState } from 'react';
import { useLocale } from '../LocaleContext.tsx';

/**
 * The beauty layer.
 *
 * Buyers meet a golden-hour still first — the asset class every competitor
 * masterplan page opens with — while the engine streams the real model
 * behind it. One tap cross-fades from the picture into the live 3D at the
 * matching camera angle, which is the moment this product stops being a
 * brochure and starts being a place.
 *
 * The still is generated demo imagery and says so on its face (§demo). It
 * renders only on a masterplan landing: a deep link into a unit goes
 * straight to the model, and once dismissed it stays dismissed for the
 * session — the veil is a first impression, not a wall.
 */

export const HERO_IMAGE = '/assets/media/hero_masterplan.jpg';

export function HeroVeil({
  projectName,
  demoLabel,
  onDismissed,
}: {
  projectName: string;
  /** Provenance line for the generated image; empty hides the chip. */
  demoLabel: string;
  onDismissed(): void;
}) {
  const { t } = useLocale();
  const [leaving, setLeaving] = useState(false);
  const [imageReady, setImageReady] = useState(false);

  const dismiss = useCallback(() => {
    if (leaving) return;
    setLeaving(true);
  }, [leaving]);

  // The fade runs on the veil itself; when it ends the parent unmounts us.
  // Guard on the target: child transitions (button hover) bubble through
  // here too, and one of those ending mid-fade must not cut the reveal.
  // Reduced motion never sees the fade — the CSS collapses it to 1ms.
  const onTransitionEnd = useCallback(
    (event: React.TransitionEvent) => {
      if (leaving && event.target === event.currentTarget) onDismissed();
    },
    [leaving, onDismissed],
  );

  // No focus steal: the skip link stays the page's first tab stop (§3, and
  // the e2e suite holds it). Escape is the one global affordance.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') dismiss();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [dismiss]);

  return (
    <div
      className="hero-veil"
      data-leaving={leaving ? 'true' : 'false'}
      onTransitionEnd={onTransitionEnd}
      onClick={dismiss}
      role="presentation"
    >
      <img
        className="hero-veil__image"
        src={HERO_IMAGE}
        alt={t('hero.alt')}
        decoding="async"
        data-ready={imageReady ? 'true' : 'false'}
        onLoad={() => setImageReady(true)}
      />
      <div className="hero-veil__scrim" aria-hidden="true" />

      {demoLabel ? <p className="hero-veil__provenance">{demoLabel}</p> : null}

      <div className="hero-veil__content">
        <p className="hero-veil__title">{projectName}</p>
        <button
          type="button"
          className="btn btn--primary hero-veil__cta"
          onClick={(event) => {
            event.stopPropagation();
            dismiss();
          }}
        >
          {t('hero.cta')}
        </button>
        <p className="hero-veil__hint">{t('hero.hint')}</p>
      </div>
    </div>
  );
}
